import { createSlice } from '@reduxjs/toolkit'
import TemplateService from '../services/templateService'
import { errorMessage } from '../services/axios/errorMessage'
import { previewBody } from '../helpers/templateDownload'
import { normaliseCommType, pinpointRow } from '../helpers/templateLibrary'

const initialState = {
    templates: [],
    template: {
        title: '',
        comm_type: '',
        template_data: '',
        comment: '',
        media_name: '',
        yellowAi_templateId: '',
    },
    templateData: '',
    templateJSON: {},
    // commType of the template currently shown in the preview dialog.
    // Owned exclusively by fetched-template reducers + clearTemplateData;
    // pages use putCommType for their own form state, which must not be
    // clobbered by a late-resolving fetch.
    commType: '',
    approverTypes: [],
    showTemplatePreview: false,
    // Id of the template the preview dialog is showing (set when its fetch
    // starts), so the dialog can name it and download it.
    previewId: '',
    // Why the preview could not load (e.g. a template with no stored body:
    // "...managed in Meta"), shown inside the preview instead of a blank one.
    previewError: '',
    // What the preview shows beyond the body, for the template `id` it was
    // fetched for: a WhatsApp row's `meta_template_data` and header media
    // `pre_signed_url`, or a Pinpoint template's `lastModified`.
    previewDetails: null,
    // Amazon Pinpoint's SMS templates as library rows, and why they could
    // not be listed ('' when they were).
    pinpointTemplates: [],
    pinpointError: '',
    error: '',
}

const templateSlice = createSlice({
    name: 'template',
    initialState,
    reducers: {
        putCommType: (state, action) => {
            state.commType = action.payload
        },
        clearTemplateData: (state) => {
            state.templateData = ''
            state.commType = ''
            state.showTemplatePreview = false
            state.previewError = ''
            state.previewId = ''
            state.previewDetails = null
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(
                TemplateService.getTemplates.fulfilled,
                (state, action) => {
                    const rows = action.payload?.data
                    // comm_type lowercased once, here, for every reader.
                    state.templates = Array.isArray(rows)
                        ? rows.map(normaliseCommType)
                        : []
                }
            )
            .addCase(TemplateService.getTemplates.rejected, (state, action) => {
                state.error = action.payload?.data
            })
            .addCase(TemplateService.createTemplate.fulfilled, (state) => {
                state.template = initialState.template
            })
            .addCase(
                TemplateService.createTemplate.rejected,
                (state, action) => {
                    // withApiError rejects with the API body itself.
                    state.error =
                        action.payload?.error ?? action.payload?.message
                }
            )
            .addCase(
                TemplateService.getTemplateData.pending,
                (state, action) => {
                    state.previewError = ''
                    // The dialog names the template and its download after it.
                    state.previewId = action.meta?.arg ?? ''
                    state.previewDetails = null
                }
            )
            .addCase(
                TemplateService.getTemplateDetails.fulfilled,
                (state, action) => {
                    // Only for the template still being previewed: a late
                    // answer for one already closed must not dress another.
                    if (action.meta?.arg !== state.previewId) return
                    const row = action.payload?.data?.template
                    state.previewDetails = {
                        id: action.meta.arg,
                        meta_template_data: row?.meta_template_data ?? null,
                        pre_signed_url: row?.pre_signed_url ?? '',
                    }
                }
            )
            .addCase(
                TemplateService.getPinpointTemplates.fulfilled,
                (state, action) => {
                    const items = action.payload?.data?.response?.Item
                    state.pinpointTemplates = Array.isArray(items)
                        ? items.filter((i) => i?.TemplateName).map(pinpointRow)
                        : []
                    state.pinpointError = ''
                }
            )
            .addCase(
                TemplateService.getPinpointTemplates.rejected,
                (state, action) => {
                    state.pinpointTemplates = []
                    state.pinpointError = errorMessage({ data: action.payload })
                }
            )
            .addCase(
                TemplateService.getPinpointTemplate.pending,
                (state, action) => {
                    if (!action.meta?.arg?.preview) return
                    state.previewError = ''
                    state.previewId = pinpointRow({
                        TemplateName: action.meta.arg.templateName,
                    }).id
                    state.previewDetails = null
                }
            )
            .addCase(
                TemplateService.getPinpointTemplate.fulfilled,
                (state, action) => {
                    if (!action.meta?.arg?.preview) return
                    const template = action.payload?.data?.response
                    state.templateData =
                        typeof template?.Body === 'string' ? template.Body : ''
                    state.commType = 'sms'
                    state.previewDetails = {
                        id: state.previewId,
                        source: 'pinpoint',
                        lastModified: template?.LastModifiedDate ?? '',
                    }
                    if (!state.templateData)
                        state.previewError =
                            'Amazon Pinpoint returned no message text for this template.'
                    state.showTemplatePreview = true
                }
            )
            .addCase(
                TemplateService.getPinpointTemplate.rejected,
                (state, action) => {
                    if (!action.meta?.arg?.preview) return
                    state.templateData = ''
                    state.previewError = errorMessage({ data: action.payload })
                    state.showTemplatePreview = true
                }
            )
            .addCase(
                TemplateService.getTemplateData.fulfilled,
                (state, action) => {
                    const response = action.payload?.data?.response
                    state.templateData = previewBody(response)
                    if (response?.comm_type) state.commType = response.comm_type
                    state.showTemplatePreview = true
                }
            )
            .addCase(
                TemplateService.getTemplateData.rejected,
                (state, action) => {
                    state.error =
                        action.payload?.message ?? action.payload?.error
                    state.templateData = ''
                    state.previewError = errorMessage({ data: action.payload })
                    // Open the preview anyway, so the reason shows in it (it
                    // stayed closed and only a toast said anything).
                    state.showTemplatePreview = true
                }
            )
            .addCase(
                TemplateService.getTemplateDataJson.fulfilled,
                (state, action) => {
                    state.templateJSON = action.payload.data.data
                }
            )
            .addCase(
                TemplateService.getApproverTypes.fulfilled,
                (state, action) => {
                    state.approverTypes = action.payload.data
                }
            )
            .addCase(TemplateService.approveTemplate.fulfilled, (state) => {
                state.showTemplatePreview = false
            })
    },
})

export const { clearTemplateData, putCommType } = templateSlice.actions

export default templateSlice.reducer
